import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

export interface State {
  systemInfoData: object | null;
  systemNameData: string | null;
  cloudStateData: object | null;
  isLoading: boolean;
  error: unknown;
}

export type GeneralSettingsStoreState = State;

class GeneralSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('general-settings');
  }

  override createInitialState() {
    return {
      systemInfoData: null,
      systemNameData: null,
      cloudStateData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const systemInfoPromise = api.system.getInfo();
      const cloudGetStatePromise = api.cloud.getState();
      const systemNamePromise = api.system.getSystemName();

      const [systemInfoData, systemNameData, cloudStateData] = await Promise.all([
        systemInfoPromise,
        systemNamePromise,
        cloudGetStatePromise,
      ]);

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        systemInfoData: systemInfoData as unknown as object,
        systemNameData: systemNameData as unknown as string,
        cloudStateData: cloudStateData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  async setSystemName(name: string) {
    const api = this.getApi();
    await api.system.setSystemName({ name });
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'general-settings');

    return { generalSettings: this.store(selectAll, shallow) };
  }
}

function selectAll(state: GeneralSettingsStoreState) {
  return {
    ...selectBase(state),
    systemInfoData: state.systemInfoData,
    systemNameData: state.systemNameData,
    cloudStateData: state.cloudStateData,
  };
}

export const generalSettingsStore = new GeneralSettingsStore();
